import { apiMiddleware } from 'redux-api-middleware'
import { routerMiddleware } from 'connected-react-router'
import callAPI from 'utils/callAPI'
import keyMirror from 'pkeymirror'
import { getCurrentLanguage, getUUID, postMessage } from 'utils'
import { encrypt } from 'utils/rsa'
import queryString from 'query-string'
import { languageChange } from 'store/siteConfig'
import _ from 'lodash'
import { updateAliFingerprint } from 'utils/initAliFingerprint'
import {
  applyDccSelectionToPayBody,
  DCC_INFO_CHOICE_ACCEPT,
  parseDccOfferOrNull,
  resolveMerchantPayAmountForDcc,
  shouldAttemptDccCheck,
} from 'utils/dccHelpers'

// ------------------------------------
// Constants
// ------------------------------------
export const IFRAME_PAY_PAGE = keyMirror(
  {
    INIT_REQUEST: null,
    INIT_SUCCESS: null,
    HANDLE_REQUEST: null,
    HANDLE_SUCCESS: null,
    TOGGLE_FETCH_STATUS: null,
    SET_DCC_PENDING: null,
    CLEAR_DCC_PENDING: null,
  },
  'IFRAME_PAY_PAGE'
)

function finalizeIframeCardPay(dispatch, payload) {
  const { bankForm, merchantReturnUrl, paymentOrderNo } = payload || {}
  if (bankForm) {
    postMessage({
      name: 'PAYMENT_SUCCESS',
      redirectURI: bankForm,
    })
    return
  }
  if (merchantReturnUrl) {
    postMessage({
      name: 'PAYMENT_SUCCESS',
      redirectURI: merchantReturnUrl,
    })
    return
  }
  if (paymentOrderNo) {
    dispatch(queryResult())
    return
  }
  postMessage({
    name: 'PAYMENT_FAILED',
    msg: 'No redirect or order result',
    traceCode: '',
  })
}

/** New-card DCC: backend requires cardToken from cache-card-info. */
export function checkDccAvailabilityWithCardTokenIframe(orderToken, cardToken) {
  return (dispatch) => {
    return dispatch(
      callAPI({
        dispatch,
        method: 'POST',
        body: { orderToken, cardToken },
        endpoint: 'cashierii/pay/v1/unauth/check-dcc-availability',
        requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
        successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
        success: function() {},
        error: function() {},
        hideProgress: true,
        errControl: false,
      })
    )
      .then((action) => parseDccOfferOrNull((action && action.payload) || {}))
      .catch(() => null)
  }
}

export function checkDccAvailabilitySavedIframe(orderToken, payMethodShortNo) {
  return (dispatch) => {
    const loginToken = sessionStorage[orderToken]
    return dispatch(
      callAPI({
        dispatch,
        method: 'POST',
        body: {
          orderToken,
          loginToken,
          payMethodShortNo,
        },
        endpoint: 'cashierii/pay/v1/unauth/check-dcc-availability',
        requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
        successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
        success: function() {},
        error: function() {},
        hideProgress: true,
        errControl: false,
      })
    )
      .then((action) => parseDccOfferOrNull((action && action.payload) || {}))
      .catch(() => null)
  }
}

export function abandonDccPaymentIframe() {
  return (dispatch) => {
    dispatch({ type: IFRAME_PAY_PAGE.CLEAR_DCC_PENDING })
    return dispatch(toggleLoading(false))
  }
}

export function completeDccPaymentIframe(choice) {
  return (dispatch, getState) => {
    const pending = getState().iframePayPageState.dccPending
    if (!pending) {
      return Promise.resolve()
    }
    dispatch({ type: IFRAME_PAY_PAGE.CLEAR_DCC_PENDING })
    dispatch(toggleLoading(true))

    if (pending.flow === 'CARD_TOKEN') {
      const body = { ...pending.cardTokenPayBody }
      if (choice === 'DCC' && pending.dccQuoteId) {
        applyDccSelectionToPayBody(body, pending, DCC_INFO_CHOICE_ACCEPT)
      }
      return dispatch(
        callAPI({
          dispatch,
          method: 'POST',
          body,
          endpoint: 'cashierii/pay/v1/unauth/card-token-pay-iframe',
          requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
          successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
          success: function() {},
          error: function(res) {
            postMessage({
              name: 'PAYMENT_FAILED',
              ...res.head,
            })
          },
        })
      )
        .then((res) => {
          finalizeIframeCardPay(dispatch, res.payload)
        })
        .catch(() => {
          dispatch(toggleLoading(false))
        })
    }

    if (pending.flow === 'CONFIRM_PAY') {
      const body = { ...pending.confirmPayBody }
      if (choice === 'DCC' && pending.dccQuoteId) {
        applyDccSelectionToPayBody(body, pending, DCC_INFO_CHOICE_ACCEPT)
      }
      return dispatch(
        callAPI({
          dispatch,
          method: 'POST',
          body,
          header: {
            'Salt-Key': pending.saltKey,
          },
          endpoint: 'cashierii/pay/v1/unauth/confirm-pay-iframe',
          requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
          successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
          success: function() {},
          error: function(res) {
            postMessage({
              name: 'PAYMENT_FAILED',
              ...res.head,
            })
          },
        })
      )
        .then((res) => {
          finalizeIframeCardPay(dispatch, res.payload)
        })
        .catch(() => {
          dispatch(toggleLoading(false))
        })
    }

    dispatch(toggleLoading(false))
    return Promise.resolve()
  }
}

// ------------------------------------
// Actions
// ------------------------------------
export function init() {
  // 下单测试商户号：200000030906
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    const validationUrl = new URLSearchParams(window.location.search).get('validationUrl')

    const body = { orderToken }
    // Pass validationUrl to backend so it can include Apple Pay in payTabs
    if (validationUrl) {
      body.validationUrl = validationUrl
    }

    dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body,
        endpoint: 'cashierii/order/v1/unauth/init-trade-iframe',
        requestAction: IFRAME_PAY_PAGE.INIT_REQUEST,
        successAction: IFRAME_PAY_PAGE.INIT_SUCCESS,
        success: function(result) {
          // Dispatch payInfo and orderNo to siteConfig for ApplePay
          dispatch({
            type: 'SITE_CONFIG_HANDLE_SUCCESS',
            payload: {
              payInfo: result.payInfo,
              orderNo: result.orderNo,
            },
          })
          if (result.loginToken) {
            sessionStorage[orderToken] = result.loginToken
            sessionStorage[`${orderToken}-loginType`] = 'customerId'
            dispatch({
              type: 'SITE_CONFIG_HANDLE_SUCCESS',
              payload: {
                loginToken: sessionStorage[orderToken],
                loginType: 'customerId',
              },
            })
          }
          // Persist init data for result page (order amount, payee, etc.) when query-trade-result has empty lastPayerInfo
          try {
            sessionStorage.setItem(
              'iframe_init_' + orderToken,
              JSON.stringify({
                payeeInfo: result.payeeInfo,
                orderDesc: result.orderDesc,
                payInfo: result.payInfo,
                orderNo: result.orderNo,
              })
            )
          } catch (e) {}
          dispatch(queryResult())
        },
        error: function(res) {
          postMessage({
            name: 'PAYMENT_FAILED',
            ...res.head,
          })
        },
      })
    )
  }
}

export function queryResult(data) {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          orderToken
        },
        errControl: true,
        hideProgress: true,
        endpoint: 'cashierii/order/v1/unauth/query-trade-result-iframe',
        requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
        successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
        success: function(result) {
          // Support both unwrapped body (from resultHandle) and raw { body } shape
          const body = result && result.body != null ? result.body : result
          const payResult = body.payResult
          const merchantReturnUrl = body.merchantReturnUrl
          const paymentOrderNo =
            body.paymentOrderNo || body.payResultInfo?.paymentOrderNo
          if (payResult === 'S' || payResult === 'F') {
            // When merchantReturnUrl is provided → notify parent so it can redirect (e.g. https://payby.somee.com/Home/returnUrl?Orderid=...)
            if (merchantReturnUrl) {
              postMessage({
                name: 'PAYMENT_SUCCESS',
                redirectURI: merchantReturnUrl,
              })
              return
            }
            // When merchantReturnUrl is not provided → stay on success page in iframe (do not redirect to external notify URL)
            window.location.href = `/iframe/result?orderToken=${orderToken}&paymentOrderNo=${paymentOrderNo || ''}`
            return
          }
          if (payResult === 'P') {
            setTimeout(() => {
              dispatch(queryResult(data))
            }, 5000)
          }
        },
        error: function(res) {
          postMessage({
            name: 'PAYMENT_FAILED',
            ...res.head
          })
        }
      })
    )
  }
}

export function getCardToken(data, saltKey) {
  return (dispatch) => {
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: data,
        header: {
          'Salt-Key': saltKey,
        },
        endpoint: 'cashierii/common/v1/unauth/cache-card-info-iframe',
        requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
        successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
        success: function(result) {},
        error: function(res) {
          postMessage({
            name: 'PAYMENT_FAILED',
            ...res.head
          })
        }
      })
    )
  }
}

export function getAppSalt(saltKey) {
  return (dispatch) => {
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          saltKey,
        },
        endpoint: 'common/getAppSalt',
        requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
        successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
        success: function(result) {},
        error: function(res) {
          postMessage({
            name: 'PAYMENT_FAILED',
            ...res.head
          })
        }
      })
    )
  }
}

export function payByCardToekn(data) {
  return (dispatch, getState) => {
    const saltKey = getUUID()
    dispatch(toggleLoading(true))
      .then(() => updateAliFingerprint())
      .then((res) => dispatch(getAppSalt(saltKey)))
      .then((res) => {
        const { salt } = res.payload
        if (!salt) {
          throw new Error(' ')
        }
        const { cvv, cardNo, expireDate, holderName, ...restData } = data
        restData.cvv = encrypt(cvv, salt)
        restData.expireDate = encrypt(expireDate, salt)
        restData.holderName = encrypt(holderName, salt)
        restData.cardNo = encrypt(cardNo.replace(/ /g, ''), salt)
        return dispatch(getCardToken(restData, saltKey))
      })
      .then((res) => {
        const { cardToken } = res.payload
        if (!cardToken) {
          throw new Error(' ')
        }
        const orderToken = data.orderToken || getState().siteConfig.orderToken
        const { isSaveCard, installmentPlanId } = data
        const payInfo = data.payInfo || _.get(data, 'siteConfig.payInfo') || getState().siteConfig.payInfo
        const body = {
          iframe: true,
          cardToken,
          orderToken,
          payInfo,
          isSaveCard,
        }
        if (installmentPlanId) {
          body.installPlanId = installmentPlanId
        }

        if (!shouldAttemptDccCheck(cardToken)) {
          return dispatch(
            callAPI({
              dispatch: dispatch,
              method: 'POST',
              body,
              endpoint: 'cashierii/pay/v1/unauth/card-token-pay-iframe',
              requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
              successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
              success: function() {},
              error: function(res) {
                postMessage({
                  name: 'PAYMENT_FAILED',
                  ...res.head,
                })
              },
            })
          )
        }

        return dispatch(checkDccAvailabilityWithCardTokenIframe(orderToken, cardToken)).then((dcc) => {
          if (dcc) {
            dispatch(toggleLoading(false))
            dispatch({
              type: IFRAME_PAY_PAGE.SET_DCC_PENDING,
              payload: {
                dccPending: {
                  flow: 'CARD_TOKEN',
                  cardTokenPayBody: body,
                  dccQuoteId: dcc.dccQuoteId,
                  merchantPayAmount: resolveMerchantPayAmountForDcc(data, getState),
                  dccTotalAmount: dcc.dccTotalAmount,
                  exchangeRate: dcc.exchangeRate,
                  markupRate: dcc.markupRate,
                },
              },
            })
            return null
          }
          return dispatch(
            callAPI({
              dispatch: dispatch,
              method: 'POST',
              body,
              endpoint: 'cashierii/pay/v1/unauth/card-token-pay-iframe',
              requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
              successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
              success: function() {},
              error: function(res) {
                postMessage({
                  name: 'PAYMENT_FAILED',
                  ...res.head,
                })
              },
            })
          )
        })
      })
      .then((res) => {
        if (!res) {
          return
        }
        finalizeIframeCardPay(dispatch, res.payload)
      })
      .catch((res) => {
        dispatch(toggleLoading(false))
      })
  }
}

export function payByCardId(data) {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    const saltKey = getUUID()
    return dispatch(toggleLoading(true))
      .then(() => updateAliFingerprint())
      .then((res) => dispatch(getAppSalt(saltKey)))
      .then((res) => {
        const { salt } = res.payload
        if (!salt) {
          throw new Error(' ')
        }

        const { cvv, payMethodShortNo, installmentPlanId } = data
        const body = {
          orderToken,
          payMethodShortNo,
          cvv: encrypt(cvv, salt),
        }
        if (installmentPlanId) {
          body.installPlanId = installmentPlanId
        }

        if (shouldAttemptDccCheck(payMethodShortNo)) {
          return dispatch(checkDccAvailabilitySavedIframe(orderToken, payMethodShortNo)).then((dcc) => {
            if (dcc) {
              dispatch(toggleLoading(false))
              const merchantPayAmount = resolveMerchantPayAmountForDcc(data, getState)
              dispatch({
                type: IFRAME_PAY_PAGE.SET_DCC_PENDING,
                payload: {
                  dccPending: {
                    flow: 'CONFIRM_PAY',
                    confirmPayBody: body,
                    saltKey,
                    dccQuoteId: dcc.dccQuoteId,
                    merchantPayAmount,
                    dccTotalAmount: dcc.dccTotalAmount,
                    exchangeRate: dcc.exchangeRate,
                    markupRate: dcc.markupRate,
                  },
                },
              })
              return null
            }
            return dispatch(
              callAPI({
                dispatch: dispatch,
                method: 'POST',
                body,
                header: {
                  'Salt-Key': saltKey,
                },
                endpoint: 'cashierii/pay/v1/unauth/confirm-pay-iframe',
                requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
                successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
                success: function(result) {},
                error: function(res) {
                  postMessage({
                    name: 'PAYMENT_FAILED',
                    ...res.head,
                  })
                },
              })
            )
          })
        }

        return dispatch(
          callAPI({
            dispatch: dispatch,
            method: 'POST',
            body,
            header: {
              'Salt-Key': saltKey,
            },
            endpoint: 'cashierii/pay/v1/unauth/confirm-pay-iframe',
            requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
            successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
            success: function(result) {},
            error: function(res) {
              postMessage({
                name: 'PAYMENT_FAILED',
                ...res.head,
              })
            },
          })
        )
      })
      .then((res) => {
        if (!res) {
          return
        }
        finalizeIframeCardPay(dispatch, res.payload)
      })
      .catch((res) => {
        dispatch(toggleLoading(false))
      })
  }
}

export function deleteCard(data) {
  return (dispatch, getState) => {
    const loginToken = getState().siteConfig.loginToken
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          ...data,
          loginToken,
        },
        endpoint: 'cashierii/order/v1/unauth/delete-pay-method',
        requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
        successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
        success: function(result) {},
      })
    )
  }
}

export function payByDevice(data) {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          orderToken,
          ...data,
        },
        endpoint: 'cashierii/pay/v1/unauth/confirm-pay-iframe',
        requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
        successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
        success: function(result) {
          if (result.bankForm) {
            postMessage({
              name: 'PAYMENT_SUCCESS',
              redirectURI: result.bankForm,
            })
            return
          }
          if (result.merchantReturnUrl) {
            postMessage({
              name: 'PAYMENT_SUCCESS',
              redirectURI: result.merchantReturnUrl,
            })
            return
          }
          if (result.paymentOrderNo) {
            dispatch(queryResult())
            return
          }
          postMessage({
            name: 'PAYMENT_FAILED',
            msg: 'No redirect or order result',
            traceCode: '',
          })
        },
        error: function(res) {
          postMessage({
            name: 'PAYMENT_FAILED',
            ...res.head,
          })
        },
      })
    ).then((res) => {
      // Ensure paymentOrderNo exists for Apple Pay session.completePayment check
      if (res.payload && !res.payload.paymentOrderNo) {
        res.payload.paymentOrderNo = res.payload.merchantReturnUrl || res.payload.bankForm || true
      }
      return res
    })
  }
}

export function applePayValidateURL(data) {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    const validationUrl = new URLSearchParams(window.location.search).get('validationUrl')
    if (validationUrl && data.params) {
      // Strip protocol (https:// or http://) from domainName
      data.params.domainName = validationUrl.replace(/^https?:\/\//, '')
    }
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          orderToken,
          ...data,
        },
        endpoint: 'cashierii/pay/v1/unauth/init',
        requestAction: IFRAME_PAY_PAGE.HANDLE_REQUEST,
        successAction: IFRAME_PAY_PAGE.HANDLE_SUCCESS,
        success: function(result) {},
      })
    )
  }
}

export function toggleLoading(isLoading) {
  return (dispatch) => {
    dispatch({
      type: IFRAME_PAY_PAGE.TOGGLE_FETCH_STATUS,
      payload: {
        loading: isLoading,
      },
    })
    return Promise.resolve()
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [IFRAME_PAY_PAGE.INIT_REQUEST]: (state, action) =>
    Object.assign({}, state, action.payload),
  [IFRAME_PAY_PAGE.INIT_SUCCESS]: (state, action) => {
    const params = queryString.parse(window.location.search)

    // Ensure payTabs exists
    if (!action.payload.payTabs) {
      action.payload.payTabs = []
    }

    // Device pay tabs are only visible if:
    // 1. Backend returns them (product is open/enabled for the merchant)
    // 2. canMakePayments check passes (handled in ContaineriFrame via devicePay.filter)
    // Apple Pay - special case: requires validationUrl for iframe domain verification
    // If backend returns Apple Pay but no validationUrl provided, remove it
    if (!params.validationUrl) {
      _.remove(action.payload.payTabs, {
        tabName: 'Apple Pay',
        tabType: 'DEVICE_PAY',
      })
    }

    // Google Pay and Samsung Pay - respect backend's payTabs
    // Only show if backend returns them (product is open)
    // canMakePayments check is done in ContaineriFrame via devicePay.filter()

    return Object.assign({}, state, action.payload)
  },
  [IFRAME_PAY_PAGE.TOGGLE_FETCH_STATUS]: (state, action) =>
    Object.assign({}, state, action.payload),
  [IFRAME_PAY_PAGE.SET_DCC_PENDING]: (state, action) =>
    Object.assign({}, state, { dccPending: action.payload.dccPending }),
  [IFRAME_PAY_PAGE.CLEAR_DCC_PENDING]: (state) => Object.assign({}, state, { dccPending: null }),
  [IFRAME_PAY_PAGE.HANDLE_REQUEST]: (state, action) => state,
  [IFRAME_PAY_PAGE.HANDLE_SUCCESS]: (state, action) => state,
}

// ------------------------------------
// Reducer
// ------------------------------------
// 根据链接上面的参数设置语言，会话级，优先级比全局设置的高
const params = queryString.parse(window.location.search)
sessionStorage.lang = params.lang || 'en'
const theme = params.theme || 'light'
document.documentElement.setAttribute('data-theme', theme)

const initialState = {}
function reducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

export default function(history) {
  return {
    id: 'iframePayPage',
    reducerMap: {
      iframePayPageState: reducer,
    },
    // middlewares when this module needed
    middlewares: [apiMiddleware, routerMiddleware(history)],
    // Actions to fire when this module is added/removed
    initialActions: [languageChange(getCurrentLanguage())],
    // finalActions: []
  }
}
